import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { followShopLocale } from 'i18n';
import { fetchStoreProfile } from 'api/store';
import { applyTheme, loadFonts, PRESETS, readDevPreset, resolveTheme } from 'theme';
import { infoPagesFor } from 'config';
import { DEFAULT_STORE } from 'utils/defaultStore';
import { hasServerTheme } from 'utils/snapshotHandoff';
import { mergeStoreProfile, readCachedStore, writeCachedStore } from 'utils/storeProfile';

const StoreContext = createContext(null);

/**
 * Loads the shop's public profile once and applies its theme.
 *
 * status:
 *   'loading'  first load in progress (store is the cached or default profile)
 *   'ready'    profile loaded from the API
 *   'error'    API unreachable or failed; the default profile is used
 *
 * `isPlaceholder` is true while we only have the default profile (nothing
 * cached and nothing from the API), so layout can show skeletons for the
 * brand instead of flashing a generic name.
 */
export function StoreProvider({ children }) {
  const { t } = useTranslation();
  const [state, setState] = useState(() => {
    const cached = readCachedStore();
    return { store: cached || DEFAULT_STORE, status: 'loading', error: null, fromCache: !!cached };
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState((s) => (s.status === 'error' ? { ...s, status: 'loading' } : s));
    fetchStoreProfile({ signal: controller.signal })
      .then((value) => {
        writeCachedStore(value);
        setState({ store: mergeStoreProfile(value), status: 'ready', error: null, fromCache: false });
      })
      .catch((error) => {
        if (error.code === 'REQUEST_CANCELED') return;
        setState((s) => ({ ...s, status: 'error', error }));
      });
    return () => controller.abort();
  }, [attempt]);

  // Development only: ?preset=<id> previews another preset (always null in
  // production builds, where the shop's own theme wins).
  const { search } = useLocation();
  const devPreset = useMemo(() => readDevPreset(search), [search]);

  // The generic name of a shop we know nothing about yet ("Toko") is ours, not
  // the seller's, so it follows the UI language; a real shop name never changes.
  const fallbackName = t('store.fallbackName');
  const store = useMemo(() => {
    let value = state.store;
    if (devPreset) value = { ...value, theme: resolveTheme(PRESETS[devPreset]) };
    if (value.identity.name === DEFAULT_STORE.identity.name && fallbackName !== value.identity.name) {
      value = { ...value, identity: { ...value.identity, name: fallbackName } };
    }
    return value;
  }, [state.store, devPreset, fallbackName]);

  // A page served by the SEO Edge Function already carries the shop's current
  // theme inline; until the API answers, the cached or default theme would
  // only repaint it with older (or generic) colors.
  const keepServerTheme = state.status !== 'ready' && !devPreset && hasServerTheme();
  useEffect(() => {
    if (keepServerTheme) return;
    applyTheme(store.theme);
    loadFonts([store.theme.typography.heading, store.theme.typography.body]);
  }, [store.theme, keepServerTheme]);

  // The shop's `locale` picks the language only until the visitor chooses one
  // with the ID / EN switch (see i18n/language.js for the precedence).
  useEffect(() => {
    followShopLocale(store.locale);
  }, [store.locale]);

  // Favicon from the shop's branding, if provided.
  useEffect(() => {
    const href = store.identity.faviconUrl;
    if (!href) return undefined;
    let link = document.querySelector('link[rel="icon"]');
    const previous = link && link.getAttribute('href');
    const previousType = link && link.getAttribute('type');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.removeAttribute('type');
    link.setAttribute('href', href);
    return () => {
      if (previous) link.setAttribute('href', previous);
      if (previousType) link.setAttribute('type', previousType);
    };
  }, [store.identity.faviconUrl]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const infoPages = useMemo(() => infoPagesFor(store.pages), [store.pages]);

  const value = useMemo(
    () => ({
      store,
      identity: store.identity,
      theme: store.theme,
      infoPages,
      // Link /artikel from the header and footer (enabled and at least one published).
      showArticles: Boolean(store.articles && store.articles.show),
      devPreset,
      status: state.status,
      error: state.error,
      isLoading: state.status === 'loading',
      isReady: state.status === 'ready',
      isUnavailable: state.status === 'error',
      isPlaceholder: state.status === 'loading' && !state.fromCache,
      reload,
    }),
    [store, state, reload, devPreset, infoPages]
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

/**
 * Access the shop profile: `const { identity, theme, status, isUnavailable } = useStore();`
 * `theme` is always complete and safe (see theme/resolveTheme.js).
 */
export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used inside <StoreProvider>');
  return ctx;
}
